import React from "react";
import { motion } from "framer-motion";
import {
  Target,
  Search,
  Instagram,
  MonitorSmartphone,
  Star,
  Filter,
  ArrowUpRight,
} from "lucide-react";
import { TIDS } from "@/lib/testIds";

const services = [
  {
    slug: "google-ads",
    title: "Google Ads & PPC",
    desc: "High-intent search campaigns tuned for new-patient volume in your zip codes, not vanity clicks.",
    icon: Target,
    tag: "Traffic",
    glow: "group-hover:shadow-[0_20px_60px_-15px_rgba(37,99,235,0.5)]",
    iconBg: "bg-blue-500/10 border-blue-400/20",
  },
  {
    slug: "seo",
    title: "Local SEO",
    desc: "Rank #1 in the map pack for the procedures that actually pay.",
    icon: Search,
    tag: "Growth",
    glow: "group-hover:shadow-[0_20px_60px_-15px_rgba(245,158,11,0.4)]",
    iconBg: "bg-amber-500/10 border-amber-400/20",
  },
  {
    slug: "social",
    title: "Social Media",
    desc: "Meta and TikTok creative built to convert scrollers into booked appointments.",
    icon: Instagram,
    tag: "Brand",
    glow: "group-hover:shadow-[0_20px_60px_-15px_rgba(217,70,239,0.4)]",
    iconBg: "bg-fuchsia-500/10 border-fuchsia-400/20",
  },
  {
    slug: "websites",
    title: "Websites That Convert",
    desc: "Fast, beautiful sites with booking flows engineered for pickup and speed.",
    icon: MonitorSmartphone,
    tag: "Foundation",
    glow: "group-hover:shadow-[0_20px_60px_-15px_rgba(6,182,212,0.4)]",
    iconBg: "bg-cyan-500/10 border-cyan-400/20",
  },
  {
    slug: "reputation",
    title: "Reputation Engine",
    desc: "Five-star review automation and reputation defense running on autopilot.",
    icon: Star,
    tag: "Trust",
    glow: "group-hover:shadow-[0_20px_60px_-15px_rgba(16,185,129,0.4)]",
    iconBg: "bg-emerald-500/10 border-emerald-400/20",
  },
  {
    slug: "funnels",
    title: "Lead Gen Funnels",
    desc: "End-to-end funnels with CRM, nurture sequences and show-up rate tracking.",
    icon: Filter,
    tag: "System",
    glow: "group-hover:shadow-[0_20px_60px_-15px_rgba(37,99,235,0.4)]",
    iconBg: "bg-blue-500/10 border-blue-400/20",
  },
];

const container = {
  hidden: {},
  show: {
    transition: { staggerChildren: 0.09, delayChildren: 0.05 },
  },
};

const card = {
  hidden: { opacity: 0, y: 28 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] },
  },
};

export default function Services() {
  return (
    <section id="services" className="py-24 md:py-32 relative">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        {/* Heading */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-16"
        >
          <div className="md:col-span-5">
            <div className="font-mono-accent text-[11px] uppercase tracking-[0.25em] text-blue-400 mb-4">
              / 01 · What we do
            </div>
            <h2 className="font-display text-4xl sm:text-5xl md:text-6xl tracking-tighter font-medium text-white leading-[1.05]">
              Every lever your practice needs, pulled by specialists.
            </h2>
          </div>
          <div className="md:col-span-6 md:col-start-7 flex items-end">
            <p className="text-lg text-white/60 leading-relaxed">
              We don&apos;t sell services. We install a growth system. Six
              interlocking channels, one accountable team, one dashboard, one
              undeniable number, <span className="text-white">new patients per week.</span>
            </p>
          </div>
        </motion.div>

        {/* Uniform, tidy grid */}
        <motion.div
          variants={container}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-60px" }}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          {services.map((s, i) => {
            const Icon = s.icon;
            return (
              <motion.article
                key={s.slug}
                variants={card}
                data-testid={TIDS.serviceCard(s.slug)}
                className={`group relative flex flex-col rounded-3xl border border-white/10 bg-white/[0.03] backdrop-blur-xl p-8 min-h-[280px] transition-[transform,border-color,box-shadow] duration-300 will-change-transform hover:-translate-y-1.5 hover:border-white/25 ${s.glow}`}
              >
                {/* index + tag row */}
                <div className="flex items-center justify-between">
                  <div
                    className={`w-12 h-12 rounded-2xl border flex items-center justify-center ${s.iconBg} transition-transform duration-300 group-hover:scale-110 group-hover:-rotate-6`}
                  >
                    <Icon className="w-5 h-5 text-white" strokeWidth={2} />
                  </div>
                  <span className="font-mono-accent text-[10px] uppercase tracking-[0.2em] text-white/40">
                    0{i + 1} · {s.tag}
                  </span>
                </div>

                {/* copy */}
                <h3 className="font-display text-2xl tracking-tight text-white mt-8">
                  {s.title}
                </h3>
                <p className="mt-3 text-[15px] text-white/55 leading-relaxed flex-1">
                  {s.desc}
                </p>

                {/* footer link */}
                <div className="mt-6 pt-6 border-t border-white/10 flex items-center gap-2 text-sm text-white/70 group-hover:text-amber-400 transition-colors duration-200">
                  <span>Learn more</span>
                  <ArrowUpRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </div>
              </motion.article>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
}
